import { Link } from "react-router-dom";
import { Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { usePersons } from "@/hooks/usePersons";
import { PersonForm } from "@/components/PersonForm";
import { Avatar } from "@/components/PersonCard";
import { ProfileEditor } from "@/components/ProfileEditor";
import { ClaimSection } from "@/components/ClaimSection";
import { ProfileNetWorth } from "@/components/ProfileNetWorth";
import { t as tr } from "@/lib/i18n";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const IMG = "https://images.unsplash.com/photo-1565893089337-4680bbb1f34e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const SignInPrompt = ({ login }) => (
  <div className="grid lg:grid-cols-2 gap-12 items-center">
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-4">{tr("Join the family")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight">{tr("Take your place in the Vats tree.")}</h1>
      <p className="mt-5 text-[#52635B] leading-relaxed max-w-lg">Sign in with your Google email, tell us who you are connected to, and three family members will confirm you. Until then, your entry stays private.</p>
      <Button onClick={login} className="mt-8 rounded-full bg-[#D96B43] hover:bg-[#C05731] h-12 px-8" data-testid="join-signin-btn">{tr("Sign in with Google to join")}</Button>
    </div>
    <img src={IMG} alt="Old family photographs" className="w-full h-[380px] object-cover rounded-[2rem]" />
  </div>
);

const MyStatus = ({ person }) => {
  const approved = person.status === "approved";
  return (
    <div className="rounded-3xl border border-[#E5DEC9] bg-white p-7 flex flex-col sm:flex-row gap-6 items-start" data-testid="my-status-card">
      <Avatar p={person} size="w-16 h-16 text-xl" />
      <div className="flex-1 space-y-3">
        <h2 className="font-serif text-2xl font-semibold">{person.name}</h2>
        {approved ? (
          <p className="flex items-center gap-2 text-[#2E7D32]" data-testid="my-status-approved"><CheckCircle2 className="w-4 h-4" />{tr("You are on the public tree.")}</p>
        ) : (
          <div className="space-y-2" data-testid="my-status-pending">
            <p className="flex items-center gap-2 text-[#F57F17]"><Clock className="w-4 h-4" />Waiting for approval — {person.approvals}/3 family approvals</p>
            <Progress value={(person.approvals / 3) * 100} className="h-2 max-w-sm" />
          </div>
        )}
        {approved && <Button asChild variant="outline" className="rounded-full" data-testid="my-lineage-btn"><Link to={`/person/${person.person_id}`}>{tr("View my lineage")}</Link></Button>}
      </div>
    </div>
  );
};

const AddRelative = ({ persons, t, user, onDone }) => {
  const [params] = useSearchParams();
  const rel = params.get("relative") || "";
  useEffect(() => {
    if (rel) setTimeout(() => document.getElementById("add-relative")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [rel]);
  return (
    <section id="add-relative" className="space-y-5 scroll-mt-24" data-testid="add-relative-section">
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{tr("Add a family member")}</h2>
      <p className="text-[#52635B]">{tr("Anyone signed in can add a relative. They appear on the tree once 3 approved family members approve.")}</p>
      <PersonForm key={rel} persons={persons} t={t} isSelf={false} defaults={{ relative_id: rel }} onDone={onDone}
        submitLabel={user.is_admin ? "Add to tree" : "Submit for approval"} allowNoRelative={user.is_admin && persons.length === 0} />
    </section>
  );
};

export default function Join() {
  const { user, loading, login, refresh } = useAuth();
  const { persons, t, reload } = usePersons();
  if (loading || persons === null) return <div className="max-w-5xl mx-auto p-8"><div className="h-64 rounded-3xl bg-[#F4EDE2] animate-pulse" /></div>;

  const done = () => { refresh(); reload(); };
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14 space-y-10">
      {!user ? <SignInPrompt login={login} /> : user.person ? (
        <>
          <MyStatus person={user.person} />
          <section className="rounded-3xl border border-[#E5DEC9] bg-white p-7 space-y-5">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{tr("Edit my profile")}</h2>
            <ProfileEditor key={user.person.person_id} p={user.person} onSaved={done} testid="my-profile-editor" />
          </section>
          {user.person.status === "approved" && <ProfileNetWorth me={user.person} persons={persons} />}
        </>
      ) : (
        <section className="space-y-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3">Welcome, {user.name}</p>
            <h1 className="font-serif text-4xl font-bold tracking-tight">{tr("Where do you belong in the family?")}</h1>
            {persons.length === 0 && user.is_admin && <p className="mt-3 text-[#52635B]">You are the admin. The tree is empty, so you can start it as the first root — or go to Admin to add the eldest ancestor first.</p>}
          </div>
          <ClaimSection persons={persons} onClaimed={done} />
          <h2 className="font-serif text-2xl font-semibold pt-2">{tr("Or add yourself as new")}</h2>
          <PersonForm persons={persons} t={t} isSelf defaults={{ name: user.name }} onDone={done} submitLabel={user.is_admin ? "Add me to the tree" : "Submit for approval"} allowNoRelative={user.is_admin && persons.length === 0} />
        </section>
      )}
      {user && <AddRelative persons={persons} t={t} user={user} onDone={done} />}
    </div>
  );
}
